import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { AuthUser, LoginRequest } from '@/types'
import { authApi } from '@/api/services'
import {
  scheduleAccessTokenRefresh,
  clearRefreshSchedule,
  revokeRefreshToken,
  parseApiDateTimeToIso,
} from '@/api/client'

interface AuthContextValue {
  user: AuthUser | null
  login: (data: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    const u = JSON.parse(raw) as Partial<AuthUser>
    if (!u.id || !u.token) return null
    return {
      id: u.id,
      username: u.username ?? '',
      token: u.token,
      expiresAt: u.expiresAt ?? localStorage.getItem('tokenExpiresAt') ?? '',
      refreshToken: u.refreshToken ?? localStorage.getItem('refreshToken') ?? '',
      refreshTokenExpiresAt:
        u.refreshTokenExpiresAt ?? localStorage.getItem('refreshTokenExpiresAt') ?? '',
    }
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadUser)

  useEffect(() => {
    const onSessionRefreshed = () => {
      setUser(loadUser())
    }
    window.addEventListener('report-studio:session-refreshed', onSessionRefreshed)
    return () => window.removeEventListener('report-studio:session-refreshed', onSessionRefreshed)
  }, [])

  useEffect(() => {
    if (user?.refreshToken && user.expiresAt) {
      scheduleAccessTokenRefresh()
    } else {
      clearRefreshSchedule()
    }
  }, [user?.expiresAt, user?.refreshToken])

  const login = useCallback(async (data: LoginRequest) => {
    const res = await authApi.login(data)
    const accessIso = parseApiDateTimeToIso(res.expiresAt) ?? ''
    const refreshRtIso = parseApiDateTimeToIso(res.refreshTokenExpiresAt) ?? ''
    const authUser: AuthUser = {
      id: res.id,
      username: res.userName,
      token: res.token,
      expiresAt: accessIso,
      refreshToken: res.refreshToken ?? '',
      refreshTokenExpiresAt: refreshRtIso,
    }
    localStorage.setItem('token', res.token)
    if (res.refreshToken) {
      localStorage.setItem('refreshToken', res.refreshToken)
    }
    if (accessIso) {
      localStorage.setItem('tokenExpiresAt', accessIso)
    }
    if (refreshRtIso) {
      localStorage.setItem('refreshTokenExpiresAt', refreshRtIso)
    }
    localStorage.setItem('user', JSON.stringify(authUser))
    setUser(authUser)
  }, [])

  const logout = useCallback(async () => {
    const rt = localStorage.getItem('refreshToken')
    clearRefreshSchedule()
    if (rt) {
      try {
        await revokeRefreshToken(rt)
      } catch {
        // still clear local session
      }
    }
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('tokenExpiresAt')
    localStorage.removeItem('refreshTokenExpiresAt')
    localStorage.removeItem('user')
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
